import React, { useCallback, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import './Chart.css';
import { getResponsiveWidgetScale } from '../utils/widgetResponsive';
import {
  normalizePeriodColumnPeriod,
  normalizePeriodColumnStat,
} from '../utils/periodColumnWidget';
import { useAppTheme } from '../context/ThemeContext';
import { resolveValueColorFromStops } from './liquidShared';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
);

const formatLabel = (rawValue, period) => {
  const timestamp = Date.parse(rawValue);
  if (!Number.isFinite(timestamp)) return String(rawValue || '');

  const date = new Date(timestamp);

  const formatter = period === 'month'
    ? new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' })
    : new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

  return formatter.format(date);
};

const PLACEHOLDER_PERIOD_XDATA = [
  '2025-01-01T00:00:00.000Z',
  '2025-01-02T00:00:00.000Z',
  '2025-01-03T00:00:00.000Z',
  '2025-01-04T00:00:00.000Z',
];

const PLACEHOLDER_PERIOD_YDATA = [18, 24, 16, 27];

const isPlaceholderPeriodSeries = (id, xData, yData) => (
  String(id || '') !== 'base-period-column'
  && Array.isArray(xData)
  && Array.isArray(yData)
  && xData.length === PLACEHOLDER_PERIOD_XDATA.length
  && yData.length === PLACEHOLDER_PERIOD_YDATA.length
  && xData.every((item, index) => item === PLACEHOLDER_PERIOD_XDATA[index])
  && yData.every((item, index) => Number(item) === PLACEHOLDER_PERIOD_YDATA[index])
);

const normalizeChartFontSize = (value, fallback) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return fallback;
  return Math.max(6, Math.min(160, Math.round(numericValue)));
};

const PeriodColumnChart = ({
  id,
  title,
  transparentBackground,
  dims = { width: 850, height: 550 },
  draggable,
  xData = [],
  yData = [],
  period = 'day',
  stat = 'max',
  color,
  colors = [],
  colorGrading = false,
  changeColorBasedOnValue = false,
  unit,
  showUnit = true,
  pin,
  datastreams = [],
  decimals,
  labelFontSize,
  valueFontSize,
}) => {
  const { mode } = useAppTheme();
  const [cursorState, setCursorState] = useState('idle');
  const normalizedPeriod = normalizePeriodColumnPeriod(period);
  const normalizedStat = normalizePeriodColumnStat(stat);
  const safeXData = useMemo(
    () => (isPlaceholderPeriodSeries(id, xData, yData) ? [] : xData),
    [id, xData, yData]
  );
  const safeYData = useMemo(
    () => (isPlaceholderPeriodSeries(id, xData, yData) ? [] : yData),
    [id, xData, yData]
  );

  const datastreamMeta = useMemo(() => {
    const list = Array.isArray(datastreams) ? datastreams : [];
    if (!pin) return null;
    return list.find((item) => String(item?.pin || '') === String(pin)) || null;
  }, [datastreams, pin]);

  const resolvedUnit = useMemo(() => {
    const datastreamUnit = String(datastreamMeta?.unit || '').trim();
    if (datastreamUnit) return datastreamUnit;
    return String(unit || '').trim();
  }, [datastreamMeta, unit]);

  const responsiveTextScale = useMemo(
    () => getResponsiveWidgetScale(dims, { baseWidth: 320, baseHeight: 220, min: 0.82, max: 2.4 }),
    [dims]
  );
  const baseChartFontSize = Math.max(11, Math.min(24, 12 * responsiveTextScale));
  const labelFontSizePx = normalizeChartFontSize(labelFontSize, baseChartFontSize);
  const valueFontSizePx = normalizeChartFontSize(valueFontSize, baseChartFontSize);
  const chartTitleFontSizePx = normalizeChartFontSize(
    labelFontSize,
    Math.max(14, Math.min(42, 18 * responsiveTextScale))
  );

  const cssVar = useCallback((name, fallback) =>
    getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim() || fallback
  , []);

  const valuePrecision = useMemo(() => {
    const parsed = Number(decimals);
    if (!Number.isInteger(parsed)) return null;
    return Math.max(0, Math.min(parsed, 6));
  }, [decimals]);

  const labels = useMemo(
    () => (Array.isArray(safeXData) ? safeXData.map((item) => formatLabel(item, normalizedPeriod)) : []),
    [safeXData, normalizedPeriod]
  );

  const values = useMemo(
    () => (Array.isArray(safeYData) ? safeYData.map((item) => {
      const numeric = Number(item);
      return Number.isFinite(numeric) ? numeric : null;
    }) : []),
    [safeYData]
  );

  const datasetColors = useMemo(
    () => values.map((value) => (
      resolveValueColorFromStops({
        value,
        baseColor: color || '#3b82f6',
        stops: colors,
        enabled: changeColorBasedOnValue,
        grading: colorGrading,
      }) || color || '#3b82f6'
    )),
    [values, color, colors, changeColorBasedOnValue, colorGrading]
  );

  const data = useMemo(() => ({
    labels,
    datasets: [
      {
        label: normalizedStat === 'min' ? 'Min' : 'Max',
        data: values,
        backgroundColor: datasetColors,
        borderColor: datasetColors,
        borderWidth: 1,
        borderRadius: 8,
        borderSkipped: false,
        maxBarThickness: 42,
      },
    ],
  }), [datasetColors, labels, normalizedStat, values]);

  const options = useMemo(() => {
    const mutedFallback = mode === 'light' ? '#64748b' : '#64748b';
    const legendFallback = mode === 'light' ? '#64748b' : '#94a3b8';
    const gridXFallback = mode === 'light' ? 'rgba(148, 163, 184, 0.22)' : 'rgba(51, 65, 85, 0.2)';
    const gridYFallback = mode === 'light' ? 'rgba(148, 163, 184, 0.34)' : 'rgba(51, 65, 85, 0.35)';

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          titleFont: { size: labelFontSizePx },
          bodyFont: { size: valueFontSizePx },
          callbacks: {
            title: (items) => items?.[0]?.label || '',
            label: (context) => {
              const rawValue = Number(context?.parsed?.y);
              const formattedValue = Number.isFinite(rawValue)
                ? rawValue.toLocaleString(undefined, valuePrecision == null
                  ? undefined
                  : {
                      minimumFractionDigits: valuePrecision,
                      maximumFractionDigits: valuePrecision,
                    })
                : 'N/A';
              const statLabel = normalizedStat === 'min' ? 'Min' : 'Max';
              return showUnit && resolvedUnit
                ? `${statLabel}: ${formattedValue} ${resolvedUnit}`
                : `${statLabel}: ${formattedValue}`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: cssVar('--chart-muted', mutedFallback),
            maxRotation: 0,
            autoSkip: true,
            font: { size: labelFontSizePx },
          },
          grid: {
            color: cssVar('--chart-grid-x', gridXFallback),
            drawTicks: false,
          },
        },
        y: {
          beginAtZero: false,
          grace: '10%',
          ticks: {
            color: cssVar('--chart-legend', legendFallback),
            font: { size: valueFontSizePx },
            callback: (value) => Number(value).toLocaleString(),
          },
          grid: {
            color: cssVar('--chart-grid-y', gridYFallback),
          },
        },
      },
    };
  }, [
    cssVar,
    mode,
    normalizedStat,
    resolvedUnit,
    labelFontSizePx,
    showUnit,
    valueFontSizePx,
    valuePrecision,
  ]);

  const styleVars = useMemo(() => {
    const width = typeof dims?.width === 'number' ? `${dims.width}px` : (dims?.width || '850px');
    const height = typeof dims?.height === 'number' ? `${dims.height}px` : (dims?.height || '550px');
    const themeBackground = cssVar('--header-background', mode === 'light' ? '#F6FFFB' : '#131b2f');
    const background =
      typeof transparentBackground === 'string' && transparentBackground.trim() !== ''
        ? transparentBackground
        : (transparentBackground
          ? 'transparent'
          : themeBackground);

    return {
      '--chart-width': width,
      '--chart-height': height,
      '--chart-bg-effective': background,
      '--chart-pointer-events': draggable ? 'none' : 'auto',
      '--chart-title-size': `${chartTitleFontSizePx}px`,
      '--chart-empty-size': `${normalizeChartFontSize(valueFontSize, Math.max(12, Math.min(24, 14 * responsiveTextScale)))}px`,
    };
  }, [chartTitleFontSizePx, cssVar, dims, draggable, mode, responsiveTextScale, transparentBackground, valueFontSize]);

  const cursorClass = draggable
    ? (cursorState === 'grabbing' ? 'chart--grabbing' : 'chart--grab')
    : 'chart--default';

  const handleDragStart = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const offsetX = event.clientX - rect.x;
    const offsetY = event.clientY - rect.y;
    let dragData = `period-column;${id};${offsetX};${offsetY};`;
    dragData += id && id.toString().startsWith('base') ? 'add' : 'move';
    event.dataTransfer.setData('text/plain', dragData);
  };

  return (
    <div
      className={[
        'widget-container',
        'chart-container',
        transparentBackground === true ? 'chart--transparent' : '',
        cursorClass,
      ].filter(Boolean).join(' ')}
      draggable={draggable}
      onDragStart={draggable ? handleDragStart : undefined}
      onMouseDown={() => draggable && setCursorState('grabbing')}
      onMouseUp={() => draggable && setCursorState('grab')}
      onMouseLeave={() => draggable && setCursorState('grab')}
      style={styleVars}
    >
      <div className="chart-header">
        <h3 className="chart-title">{title}</h3>
      </div>

      <div className="chart-body">
        {labels.length > 0 && values.some((value) => value !== null) ? (
          <Bar data={data} options={options} />
        ) : (
          <div className="chart-emptyText">
            Waiting for sensor data...
          </div>
        )}
      </div>
    </div>
  );
};

export default PeriodColumnChart;
